import { all, put, select, takeLatest } from 'redux-saga/effects';
import { AlertActionType } from '@store/alert/alert-action';
import { BasketActionTypes } from '@store/basket/basket-actions';
import getBasketData from '@api/shop/get-basket-data';
import finalizeBasket from '@api/shop/finalize-basket';
import { basketStore } from '@store/basket/basket-store';
import { setNewAddressWatcher } from '@store/client/contact-information/contact-information-middleware';
import addAndIncrementItemToBasket from '@api/shop/add-and-increment-item-to-basket';
import decrementItemFromBasket from '@api/shop/decrement-item-from-basket';
import removeItemFromBasket from '@api/shop/remove-item-from-basket';
import ReactGA from 'react-ga4';
import { homeStore } from '@store/home-screen/home-store';

function* getBasketInfoWatcher() {
  try {
    const response: { data: { data: any } } = yield getBasketData({ cache: 'no-cache' });
    if (response) {
      yield put({
        type: BasketActionTypes.SET_BASKET_INFO,
        data: response,
      });
    }
  } catch (error: any) {
    yield put({
      type: BasketActionTypes.SET_BASKET_LOADING,
      data: {
        loading: false,
      },
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات سبد خرید شما مشکلی پیش آمده است، لطفا مجددا تلاش کنید.',
      severity: 'danger',
    });
  }
}

function* addAndIncrementItemToBasketWatcher() {
  try {
    const { productId } = yield select(basketStore);
    const response: { data: { data: any } } = yield addAndIncrementItemToBasket({ productId: productId, cache: 'no-cache' });

    if (response) {
      yield put({
        type: BasketActionTypes.SET_BASKET_INFO,
        data: response,
      });
      ReactGA.ga('send', 'event', 'Product', 'Add to cart', 'product add to cart general');
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'افزودن کالا به سبد خرید با مشکل مواجه شد، لطفا مجددا تلاش کنید.',
      severity: 'danger',
    });
  }
  yield put({ type: BasketActionTypes.SET_ADD_OR_REMOVE_LOADING });
}

function* decrementItemFromBasketWatcher() {
  try {
    const { productId } = yield select(basketStore);
    const response: { data: { data: any } } = yield decrementItemFromBasket({ productId: productId, cache: 'no-cache' });

    if (response) {
      yield put({
        type: BasketActionTypes.SET_BASKET_INFO,
        data: response,
      });
      yield put({ type: BasketActionTypes.SET_ADD_OR_REMOVE_LOADING });
    } else {
      yield put({ type: BasketActionTypes.RESET_BASKET_INFO });
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'حذف کالا از سبد خرید با مشکل مواجه شد، لطفا مجددا تلاش کنید.',
      severity: 'danger',
    });
  }
  yield put({ type: BasketActionTypes.SET_ADD_OR_REMOVE_LOADING });
}

function* removeItemFromBasketWatcher() {
  try {
    const { productId } = yield select(basketStore);
    const response: { data: { data: any } } = yield removeItemFromBasket({ productId: productId, cache: 'no-cache' });

    if (response) {
      yield put({
        type: BasketActionTypes.SET_BASKET_INFO,
        data: response,
      });
      yield put({ type: BasketActionTypes.SET_ADD_OR_REMOVE_LOADING });
    } else {
      yield put({ type: BasketActionTypes.RESET_BASKET_INFO });
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'حذف کالا از سبد خرید با مشکل مواجه شد، لطفا مجددا تلاش کنید.',
      severity: 'danger',
    });
  }
  yield put({ type: BasketActionTypes.SET_ADD_OR_REMOVE_LOADING });
}

function* finalizeBasketWatcher() {
  try {
    const { routeKey, recipients, contactInfo: { addressesList }, clientSuggestedType, clientSuggestedTime, clientSuggestedRange, auth: { firstName, lastName, nationalId, sex } } = yield select(basketStore);
    const { navigation } = yield select(homeStore);

    if (!addressesList.length) {
      yield setNewAddressWatcher();
    }

    const { contactInfo: { selectedAddressId } } = yield select(basketStore);

    const response: { data: { route_key: string }, status: boolean } = yield finalizeBasket({
      basketId: routeKey,
      clientContactId: selectedAddressId,
      clientSuggestedType: clientSuggestedType,
      clientSuggestedTime: clientSuggestedTime,
      clientSuggestedRange: clientSuggestedRange,
      gender: sex,
      applicantName: firstName,
      applicantLastName: lastName,
      applicantCode: nationalId,
      contributors: recipients,
      cache: 'no-cache',
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: 'اطلاعات نسخه داروخانه شما با موفقیت ذخیره شد.',
      severity: 'success',
    });
    //ReactGA.ga('send', 'event', 'shop', 'preorder', 'register a shop order');
    if (response?.status) {
      ReactGA.ga('send', 'event', 'Product', 'Preorder', 'product preorder general');
      if (window.location.pathname.startsWith('/mobile')) {
        yield navigation.push( `/mobile/financials/order/${response?.data?.route_key}` );
      } else {
        yield navigation.push( `/financials/order/${response?.data?.route_key}` );
      }
      yield put({ type: BasketActionTypes.RESET_BASKET_INFO });
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || 'در گرفتن اطلاعات سبد خرید شما مشکلی پیش آمده است، لطفا مجددا تلاش کنید.',
      severity: 'danger',
    });
  }
}

function* basketMiddleware() {
  yield all([
    takeLatest(BasketActionTypes.GET_BASKET_INFO, getBasketInfoWatcher),
    takeLatest(BasketActionTypes.ADD_AND_INCREMENT_ITEM_TO_BASKET, addAndIncrementItemToBasketWatcher),
    takeLatest(BasketActionTypes.DECREMENT_ITEM_FROM_BASKET, decrementItemFromBasketWatcher),
    takeLatest(BasketActionTypes.REMOVE_ITEM_FROM_BASKET, removeItemFromBasketWatcher),
    takeLatest(BasketActionTypes.FINALIZE_BASKET, finalizeBasketWatcher),
  ]);
}

export default basketMiddleware;
